import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { PermissionRequests } from '@/pages/agents';
import { Badge, Card, Empty } from '@/components/ui';
import { api } from '@/lib/api';
import { useDetail } from '@/lib/hooks';
import { fmtDateTime } from '@/lib/utils';
import { HealthPanel } from '@/components/health-panel';

const LABEL = { daily_prep: 'Daily prep', weekly_review: 'Weekly review' } as const;
const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());

export function DashboardPage() {
  const { open } = useDetail();
  const { data: focus } = useQuery({
    queryKey: ['focus', todayLocal()],
    queryFn: () => api.focus(todayLocal()),
  });
  const { data: rituals } = useQuery({ queryKey: ['upcoming', 8], queryFn: () => api.upcoming(8) });
  const { data: buddy } = useQuery({ queryKey: ['buddy', true], queryFn: () => api.buddy(true) });
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Overview</h1>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-semibold">Today's focus</h2>
            <Link to="/" className="text-sm text-primary hover:underline">
              Open
            </Link>
          </div>
          {!focus?.items.length ? (
            <Empty>No focus set for today.</Empty>
          ) : (
            <ol className="list-decimal space-y-1 pl-5 text-sm">
              {focus.items.map((i) => (
                <li key={i.node.id} className={i.node.status === 'done' ? 'text-muted-fg line-through' : ''}>
                  <button className="text-left hover:underline" onClick={() => open(i.node.id)}>
                    {i.node.title}
                  </button>
                </li>
              ))}
            </ol>
          )}
        </Card>
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-semibold">Upcoming rituals</h2>
            <span className="flex gap-2 text-sm">
              <Link to="/review/daily" className="text-primary hover:underline">
                Start daily prep
              </Link>
              <Link to="/review/weekly" className="text-primary hover:underline">
                Weekly review
              </Link>
            </span>
          </div>
          <ul className="space-y-1 text-sm">
            {rituals?.slice(0, 5).map((r) => (
              <li key={r.schedule_id + r.at} className="flex justify-between">
                <span>
                  {LABEL[r.type]} {r.overridden && <Badge tone="warn">moved</Badge>}
                </span>
                <span className="text-muted-fg">{fmtDateTime(r.at)}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="p-4">
          <h2 className="mb-2 font-semibold">Buddy check-ins due</h2>
          {!buddy?.items.length ? (
            <Empty>No buddy items due.</Empty>
          ) : (
            <ul className="space-y-2 text-sm">
              {buddy.items.map((b) => (
                <li key={b.id}>
                  <button className="text-left font-medium hover:underline" onClick={() => open(b.id)}>
                    {b.title}
                  </button>
                  {b.buddy_note && <div className="text-xs text-muted-fg">{b.buddy_note}</div>}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-4">
          <h2 className="mb-2 font-semibold">Pending permissions</h2>
          <PermissionRequests />
        </Card>
      </div>
      <HealthPanel />
    </div>
  );
}
